import React, { useState, useRef } from 'react';
import api, { handleAPIError } from '../../services/api';

interface AuditRow {
  _url: string;
  _clicks: number;
  _impressions: number;
  _backlinks: number;
  _isBlog: boolean;
  _isOnGoogle: boolean;
  _action: 'KEEP' | 'DELETE' | 'REDIRECT' | 'UPGRADE';
  _actionReason: string;
}

interface ClickBucket {
  range: string;
  count: number;
}

interface ContentAuditResponse {
  totalRows: number;
  blogCount: number;
  nonBlogCount: number;
  indexedCount: number;
  notIndexedCount: number;
  actionCounts: {
    KEEP: number;
    DELETE: number;
    REDIRECT: number;
    UPGRADE: number;
  };
  clickBuckets: ClickBucket[];
  rows: AuditRow[];
}

type ActionType = 'ALL' | 'KEEP' | 'DELETE' | 'REDIRECT' | 'UPGRADE';

const ACTION_COLORS: Record<string, { bg: string; text: string; badge: string; row: string }> = {
  KEEP: {
    bg: 'bg-green-500',
    text: 'text-green-800',
    badge: 'bg-green-100 text-green-800 border-green-200',
    row: 'bg-green-50',
  },
  DELETE: {
    bg: 'bg-red-500',
    text: 'text-red-800',
    badge: 'bg-red-100 text-red-800 border-red-200',
    row: 'bg-red-50',
  },
  REDIRECT: {
    bg: 'bg-yellow-500',
    text: 'text-yellow-800',
    badge: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    row: 'bg-yellow-50',
  },
  UPGRADE: {
    bg: 'bg-blue-500',
    text: 'text-blue-800',
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    row: 'bg-blue-50',
  },
};

const ContentAudit: React.FC = () => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ContentAuditResponse | null>(null);
  const [filterAction, setFilterAction] = useState<ActionType>('ALL');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!csvFile || !sitemapUrl.trim()) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const csvData = await readFileAsText(csvFile);
      const response = await api.post<ContentAuditResponse>('/seotools/content-audit', {
        csvData,
        sitemapUrl: sitemapUrl.trim(),
      });
      setResult(response.data);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const filteredRows = result
    ? filterAction === 'ALL'
      ? result.rows
      : result.rows.filter((r) => r._action === filterAction)
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800">Content Audit</h2>
        <p className="text-sm text-gray-500 mt-1">
          Upload a GSC CSV export and provide your sitemap URL to audit content performance and get action recommendations.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-lg shadow border border-gray-200">
        <div>
          <label htmlFor="csv-upload" className="block text-sm font-medium text-gray-700 mb-1">
            CSV File <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              id="csv-upload"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 file:cursor-pointer"
              disabled={loading}
            />
            {csvFile && (
              <span className="text-xs text-gray-500 shrink-0">
                {csvFile.name} ({(csvFile.size / 1024).toFixed(1)} KB)
              </span>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="sitemap-url" className="block text-sm font-medium text-gray-700 mb-1">
            Sitemap URL <span className="text-red-500">*</span>
          </label>
          <input
            id="sitemap-url"
            type="url"
            value={sitemapUrl}
            onChange={(e) => setSitemapUrl(e.target.value)}
            placeholder="https://example.com/sitemap.xml"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            required
            disabled={loading}
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading || !csvFile || !sitemapUrl.trim()}
            className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Auditing...
              </span>
            ) : (
              'Run Content Audit'
            )}
          </button>
        </div>

        {loading && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
            This may take a while as the sitemap needs to be fetched and each URL checked for indexing status. Please be patient.
          </p>
        )}
      </form>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <strong>Error:</strong> {error}
        </div>
      )}

      {result && (
        <div className="space-y-6">
          {/* Summary stats cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-gray-800">{result.totalRows}</div>
              <div className="text-xs text-gray-500 mt-1">Total Rows</div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-blue-700">{result.blogCount}</div>
              <div className="text-xs text-gray-500 mt-1">Blog Pages</div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-purple-700">{result.nonBlogCount}</div>
              <div className="text-xs text-gray-500 mt-1">Non-Blog Pages</div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4 text-center">
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-2xl font-bold text-green-700">{result.indexedCount}</span>
                <span className="text-gray-400">/</span>
                <span className="text-lg font-semibold text-red-600">{result.notIndexedCount}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">Indexed / Not Indexed</div>
            </div>
          </div>

          {/* Action distribution badges */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Action Distribution</h3>
            <div className="flex flex-wrap gap-3">
              {(Object.keys(ACTION_COLORS) as Array<keyof typeof ACTION_COLORS>).map((action) => (
                <button
                  key={action}
                  onClick={() => setFilterAction(filterAction === action ? 'ALL' : (action as ActionType))}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold transition-all cursor-pointer ${
                    ACTION_COLORS[action].badge
                  } ${filterAction === action ? 'ring-2 ring-offset-1 ring-gray-400' : ''}`}
                >
                  <span
                    className={`inline-block w-3 h-3 rounded-full ${ACTION_COLORS[action].bg}`}
                  />
                  {action}: {result.actionCounts[action as keyof typeof result.actionCounts]}
                </button>
              ))}
              {filterAction !== 'ALL' && (
                <button
                  onClick={() => setFilterAction('ALL')}
                  className="inline-flex items-center px-3 py-2 rounded-full border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Show All
                </button>
              )}
            </div>
          </div>

          {/* Click distribution */}
          {result.clickBuckets && result.clickBuckets.length > 0 && (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Click Distribution</h3>
              <div className="space-y-2">
                {result.clickBuckets.map((bucket, i) => {
                  const maxCount = Math.max(...result.clickBuckets.map((b) => b.count), 1);
                  const widthPct = (bucket.count / maxCount) * 100;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-28 text-xs text-gray-600 text-right shrink-0 font-mono">
                        {bucket.range}
                      </div>
                      <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                        <div
                          className="bg-blue-500 h-full rounded-full transition-all"
                          style={{ width: `${widthPct}%` }}
                        />
                      </div>
                      <div className="w-12 text-xs text-gray-700 font-medium">{bucket.count}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Results table */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">
                Results{' '}
                <span className="text-gray-400 font-normal">
                  ({filteredRows.length} {filteredRows.length === 1 ? 'row' : 'rows'}
                  {filterAction !== 'ALL' ? ` - filtered by ${filterAction}` : ''})
                </span>
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-3 py-2 font-medium text-gray-600">URL</th>
                    <th className="px-3 py-2 font-medium text-gray-600 text-center">Clicks</th>
                    <th className="px-3 py-2 font-medium text-gray-600 text-center">Impressions</th>
                    <th className="px-3 py-2 font-medium text-gray-600 text-center">Backlinks</th>
                    <th className="px-3 py-2 font-medium text-gray-600 text-center">Blog?</th>
                    <th className="px-3 py-2 font-medium text-gray-600 text-center">Indexed?</th>
                    <th className="px-3 py-2 font-medium text-gray-600 text-center">Action</th>
                    <th className="px-3 py-2 font-medium text-gray-600">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredRows.map((row, i) => {
                    const colors = ACTION_COLORS[row._action] || ACTION_COLORS.KEEP;
                    return (
                      <tr key={i} className={colors.row}>
                        <td className="px-3 py-2 max-w-[300px] truncate" title={row._url}>
                          <a
                            href={row._url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {row._url.replace(/^https?:\/\//, '')}
                          </a>
                        </td>
                        <td className="px-3 py-2 text-center font-medium">{row._clicks}</td>
                        <td className="px-3 py-2 text-center">{row._impressions}</td>
                        <td className="px-3 py-2 text-center">{row._backlinks}</td>
                        <td className="px-3 py-2 text-center">
                          {row._isBlog ? (
                            <span className="text-blue-600 font-medium">Yes</span>
                          ) : (
                            <span className="text-gray-400">No</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {row._isOnGoogle ? (
                            <span className="text-green-600 font-medium">Yes</span>
                          ) : (
                            <span className="text-red-500 font-medium">No</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span
                            className={`inline-block px-2.5 py-0.5 text-xs font-semibold rounded-full border ${colors.badge}`}
                          >
                            {row._action}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600 text-xs max-w-[250px]" title={row._actionReason}>
                          {row._actionReason}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredRows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-8 text-center text-gray-400">
                        No rows match the current filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentAudit;
